import { SurfaceBadge } from '@/components/match/surface-badge';
import {
  formatStartsAt,
  levelChipClass,
  marketLabel,
  selectionLabel,
  shortLevel,
} from '@/components/opportunity/card-formatters';
import { EditPickDialog } from '@/components/opportunity/edit-pick-dialog';
import { PerBookGrid } from '@/components/opportunity/per-book-grid';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { edgeColorClass, formatEdge } from '@/lib/format-edge';
import { formatOdds } from '@/lib/format-odds';
import { formatCountdown, formatRelativeTime } from '@/lib/format-relative-time';
import { cn } from '@/lib/utils';
import type { TrackedSignalRow } from '@tennis-scanner/shared';
import { Calendar, Clock, Pencil } from 'lucide-react';
import { useState } from 'react';

// Card for one tracked pick. Always visible regardless of value status —
// even with no edge, the operator sees the target + per-book odds + edge.
//
// perBook now spans the FULL book universe (allowed ∪ reference). Allowed
// books render with green/red coding (operator can act); reference books
// render dimmed as market context. See PerBookGrid.
//
// Match metadata (player names, tournament, surface, start time) comes
// embedded in the tracked signal — no separate matches.search lookup
// needed.

interface Props {
  tracked: TrackedSignalRow;
}

// Optional operator-entered stake (signal.metadata.manualStakeUsd). When
// the pick becomes an opportunity, the dashboard surfaces this side by side
// with the Kelly recommendation; until then this inline badge is the only
// place it shows up.
function readManualStake(metadata: Record<string, unknown>): number | null {
  const raw = metadata.manualStakeUsd;
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0) return null;
  return raw;
}

export function TrackedPickCard({ tracked }: Props) {
  const { signal, match, perBook, bestBook, bestOdds, bestEdgePct, bestObservedAt } = tracked;
  const [editing, setEditing] = useState(false);
  const matchTitle =
    match.p1Name && match.p2Name
      ? `${match.p1Name} vs ${match.p2Name}`
      : `Match ${match.matchId.slice(0, 8)}`;
  const tournamentDisplay = match.tournamentName ?? 'Torneo desconocido';
  const hasValue = bestEdgePct !== null && bestEdgePct >= 0;
  const sourceLabel =
    typeof signal.metadata?.source === 'string' ? signal.metadata.source : signal.modelId;
  const validUntilMs = Date.parse(signal.validUntil);
  const expiresIn = Number.isFinite(validUntilMs) ? formatCountdown(signal.validUntil) : '—';
  const manualStakeUsd = readManualStake(signal.metadata ?? {});
  const isManual = signal.modelId === 'manual';
  // Honest freshness: youngest observedAt across the per-book grid (real
  // market data age, not scanner cycle metric).
  const freshestObservedAt = freshestAcross(perBook);

  return (
    <Card className={cn('p-4 transition-colors', hasValue && 'border-positive/40')}>
      <Header match={match} tournamentDisplay={tournamentDisplay} expiresIn={expiresIn} />
      <Body
        match={match}
        signal={signal}
        matchTitle={matchTitle}
        manualStakeUsd={manualStakeUsd}
        sourceLabel={sourceLabel}
        bestBook={bestBook}
        bestOdds={bestOdds}
        bestEdgePct={bestEdgePct}
        bestObservedAt={bestObservedAt ?? freshestObservedAt}
      />
      <PerBookGrid perBook={perBook} targetOdds={signal.targetOdds} />
      {isManual && (
        <div className="mt-3 flex items-center justify-end border-t border-border pt-3">
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            <Pencil className="size-3.5" />
            Corregir
          </Button>
        </div>
      )}
      <EditPickDialog signal={editing ? signal : null} onClose={() => setEditing(false)} />
    </Card>
  );
}

function Header({
  match,
  tournamentDisplay,
  expiresIn,
}: {
  match: TrackedSignalRow['match'];
  tournamentDisplay: string;
  expiresIn: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
      {match.tournamentLevel && (
        <span
          className={cn('rounded-sm border px-1.5 py-0', levelChipClass(match.tournamentLevel))}
        >
          {shortLevel(match.tournamentLevel)}
        </span>
      )}
      <SurfaceBadge surface={match.surface} />
      <span className="text-foreground">{tournamentDisplay}</span>
      {match.bestOf && <span>BO{match.bestOf}</span>}
      {match.status && match.status !== 'scheduled' && <span>{match.status}</span>}
      <span className="ml-auto inline-flex items-center gap-1">
        <Calendar className="size-3" />
        {formatStartsAt(match.startsAt)}
      </span>
      <span className="inline-flex items-center gap-1">
        <Clock className="size-3" />
        vence {expiresIn}
      </span>
    </div>
  );
}

interface BodyProps {
  match: TrackedSignalRow['match'];
  signal: TrackedSignalRow['signal'];
  matchTitle: string;
  manualStakeUsd: number | null;
  sourceLabel: string;
  bestBook: string | null;
  bestOdds: number | null;
  bestEdgePct: number | null;
  bestObservedAt: string | null;
}

function Body(p: BodyProps) {
  return (
    <div className="mt-2 flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-base font-semibold text-foreground">{p.matchTitle}</h3>
        <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
          <span className="rounded-sm border border-border bg-muted px-1.5 py-0 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {marketLabel(p.signal.market)}
          </span>
          <span className="font-medium text-foreground">
            {selectionLabel(p.signal.selection, p.match)}
          </span>
          <span className="font-mono text-xs text-muted-foreground">
            cuota mín {formatOdds(p.signal.targetOdds)}
          </span>
          {p.manualStakeUsd !== null && (
            <span className="rounded-sm border border-info/30 bg-info/5 px-1.5 py-0 font-mono text-[11px] text-info">
              stake ${p.manualStakeUsd.toLocaleString('en-US')}
            </span>
          )}
          <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            fuente {p.sourceLabel}
          </span>
        </div>
      </div>

      <div className="flex flex-col items-end text-right">
        <span className={cn('font-mono text-xl font-semibold', edgeColorClass(p.bestEdgePct))}>
          {formatEdge(p.bestEdgePct)}
        </span>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="font-mono text-base font-medium text-foreground">
            {formatOdds(p.bestOdds)}
          </span>
          <span className="text-xs text-muted-foreground">{p.bestBook ?? '—'}</span>
        </div>
        {p.bestObservedAt && (
          <span className="font-mono text-[10px] text-muted-foreground">
            {formatRelativeTime(p.bestObservedAt)}
          </span>
        )}
      </div>
    </div>
  );
}

function freshestAcross(perBook: TrackedSignalRow['perBook']): string | null {
  let best: string | null = null;
  let bestT = 0;
  for (const b of perBook) {
    const t = Date.parse(b.observedAt);
    if (Number.isFinite(t) && t > bestT) {
      bestT = t;
      best = b.observedAt;
    }
  }
  return best;
}
